// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 10-security-compliance.md
// The shell's account control: the signed-in email becomes the trigger for a
// small menu carrying feedback, Shift's legal documents, and Log out.
// Logout is a server-side redirect
// endpoint (GET /auth/logout — revokes the idp_sessions row, clears the
// gl_session cookie, then redirects to GREENLIGHT_BASE_URL per docs/10), not a
// ts-rest contract op, so we navigate the browser to it rather than calling the
// typed client. A full navigation is also what we want: it drops all in-memory
// app state and lands on the post-logout redirect.
import * as React from 'react';
import { ExternalLink, LogOut, MessageSquarePlus } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Role } from '@/lib/auth';
import { LEGAL_LINKS } from '@/lib/legal';
import { cn } from '@/lib/utils';

const ROLE_LABELS: Record<Role, string> = {
  org_admin: 'Org admin',
  admin: 'Admin',
  user: 'User',
};

export interface UserMenuProps {
  email: string;
  role?: Role;
  /** Layout classes for the trigger (width/flex) supplied by the host footer. */
  className?: string;
  /** Opens the shell "Send feedback" dialog; the shell owns the dialog state. */
  onSendFeedback?: () => void;
}

export function UserMenu({ email, role, className, onSendFeedback }: UserMenuProps) {
  const logout = () => {
    // Server clears the cookie + session row and redirects; a hard navigation
    // ensures no stale authenticated UI lingers behind the redirect.
    window.location.assign('/auth/logout');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="user-menu-trigger"
          aria-label={`Account menu — ${email}`}
          className={cn(
            'flex min-w-0 items-center gap-2 rounded-[var(--radius)] px-2 h-11 md:h-9 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <span
            data-testid="user-email"
            className="flex-1 truncate text-[12px] text-muted-foreground"
          >
            {email}
          </span>
          <LogOut className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <span className="block truncate text-[13px] font-medium text-foreground">{email}</span>
          {role && <span className="block text-xs text-muted-foreground">{ROLE_LABELS[role]}</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {onSendFeedback && (
          <DropdownMenuItem data-testid="send-feedback-item" onSelect={onSendFeedback}>
            <MessageSquarePlus className="mr-2 h-4 w-4" aria-hidden /> Send feedback
          </DropdownMenuItem>
        )}
        {LEGAL_LINKS.map((link) => (
          <DropdownMenuItem key={link.href} asChild>
            <a href={link.href} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" aria-hidden /> {link.label}
            </a>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="logout-item" onSelect={logout}>
          <LogOut className="mr-2 h-4 w-4" aria-hidden /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
